/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';

export interface ConfirmOptions {
  message: string;
  confirmText: string;
  destructive?: boolean;
}

/** Shows a modal confirmation, resolving to true if the user confirmed. */
export function showConfirmation(options: ConfirmOptions): Promise<boolean> {
  return showDialog(options, true);
}

/** Shows a modal message with a single dismiss button. */
export async function showAlert(message: string): Promise<void> {
  await showDialog({ message, confirmText: 'OK' }, false);
}

function showDialog(options: ConfirmOptions, cancelable: boolean): Promise<boolean> {
  const dialog = document.createElement('yl-confirm-dialog');
  dialog.message = options.message;
  dialog.confirmText = options.confirmText;
  dialog.destructive = options.destructive ?? false;
  dialog.cancelable = cancelable;
  document.body.append(dialog);
  return dialog.show();
}

// Modal confirmation dialog, created on demand by showConfirmation().
@customElement('yl-confirm-dialog')
export class ConfirmDialog extends LitElement {
  static styles = css`
    wa-dialog {
      --width: 24rem;
    }

    p {
      margin: 0;
      line-height: 1.5;
    }
  `;

  @property({ attribute: 'message' })
  message: string = '';

  @property({ attribute: 'confirm-text' })
  confirmText: string = 'OK';

  @property({ attribute: 'destructive', type: Boolean })
  destructive: boolean = false;

  @property({ attribute: 'cancelable', type: Boolean })
  cancelable: boolean = true;

  @query('wa-dialog')
  private _dialog!: WaDialog;

  private _confirmed: boolean = false;
  private _resolve: ((confirmed: boolean) => void) | null = null;

  async show(): Promise<boolean> {
    await this.updateComplete;
    this._dialog.open = true;
    return new Promise((resolve) => (this._resolve = resolve));
  }

  private _confirm() {
    this._confirmed = true;
    this._dialog.open = false;
  }

  private _onAfterHide(e: Event) {
    if (e.target !== this._dialog) return;
    this._resolve?.(this._confirmed);
    this.remove();
  }

  render() {
    return html`<wa-dialog without-header light-dismiss label=${this.message} @wa-after-hide=${this._onAfterHide}>
      <p>${this.message}</p>
      ${this.cancelable
        ? html`<wa-button slot="footer" appearance="plain" data-dialog="close">Cancel</wa-button>`
        : null}
      <wa-button
        slot="footer"
        variant=${this.destructive ? 'danger' : 'neutral'}
        autofocus
        @click=${this._confirm}
        >${this.confirmText}</wa-button
      >
    </wa-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-confirm-dialog': ConfirmDialog;
  }
}
